import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import TranslatableText, { speak } from '../components/TranslatableText';
import { useActivityLog } from '../components/useActivityLog';
import YouTubePlayer from '../components/YouTubePlayer';
import { EXTENSION_URL, extensionInstalled, youtubeUrl } from '../lib/extension';
import { ensureAssignmentSubtitles } from '../lib/api';
import { saveCard, termKey } from '../lib/cards';
import { burst } from '../lib/confetti';
import { KIND, goesOutOn, isOut, requirement } from '../lib/kinds';
import { languageName } from '../lib/languages';
import { TOPICS, builderOnly, hasMatching, lessonSize, levelFor, levelInfo } from '../lib/grammar/topics';
import GrammarLesson from '../components/GrammarLesson';
import { translate } from '../lib/translate';
import { useLessonSave } from '../lib/lessonSaves';
import { buildStoryLesson, cleanStoryQuestions, lessonPartFor, meanings, partRange, sentences, storyModeFor, storyModesUsed, storyPlanLabel, STORY_MODE_NAME, needsReadingTime } from '../lib/storyLesson';
import type { Question } from '../lib/grammar/topics';
import { dayInZone, minutes, shortDate, today } from '../lib/dates';
import {
  DEFAULT_WPM, STILL_MAX_TRAVEL, STILL_WINDOW_MS, clock, minReadSeconds, paceLabel, paragraphMinSeconds, paragraphs, READING_IDLE_MS, wordCount,
} from '../lib/reading';
import type { Assignment, Pass, Progress } from '../lib/types';
import { useClassroom } from '../state/Classroom';
import AssignmentImages from '../components/AssignmentImages';
import AssignmentResults from '../components/AssignmentResults';

/** Share of a video that must actually PLAY for one watch to count. Mirrors the
 *  SQL in 20261005_video_subtitles.sql (log_assignment_activity). */
const VIDEO_DONE_FRACTION = 0.9;

export default function AssignmentView() {
  const { id } = useParams();
  const { hydrated, assignments, drafts, role } = useClassroom();
  if (!hydrated) return <Loading />;
  const a = assignments.find(x => x.id === id) ?? drafts.find(x => x.id === id);
  if (!a) {
    return (
      <div className="notice">
        That assignment isn't in this class. <Link to="/">Back home</Link>
      </div>
    );
  }
  return <AssignmentBody key={a.id} a={a} isStudent={role === 'student'} />;
}

const noun = (a: Assignment) => KIND[a.kind].verb;

function AssignmentBody({ a, isStudent }: { a: Assignment; isStudent: boolean }) {
  const { active, progressFor, passesFor, upsertProgress } = useClassroom();
  const progress = isStudent ? progressFor(a.id) : undefined;
  const passes = isStudent ? passesFor(a.id) : [];
  const log = useActivityLog(
    a.id, isStudent, upsertProgress, a.kind === 'story' ? READING_IDLE_MS : undefined, a.kind === 'story',
  );

  const source = active?.language ?? 'es';
  const target = active?.native_language ?? 'en';
  const done = progress?.completions ?? 0;

  // One per day: today's is done, so another can't count until tomorrow (the
  // server refuses it too). "Today" is the class's time zone, as on the server.
  const todayHere = dayInZone(new Date(), active?.timezone);
  const lockedToday = isStudent && a.one_per_day
    && passes.some(p => dayInZone(p.finished_at, active?.timezone) === todayHere);

  // Flashcards: students save from the translator popup.
  const { cards, setCards } = useClassroom();
  const savedKeys = useMemo(() => new Set(cards.map(c => termKey(c.term))), [cards]);
  const onSave = useCallback(async (term: string, translation: string) => {
    const card = await saveCard({
      term, translation, sourceLang: source, targetLang: target,
      classroomId: a.classroom_id, assignmentId: a.id,
    });
    setCards(prev => [card, ...prev]);
  }, [source, target, a.classroom_id, a.id, setCards]);
  const saver = isStudent ? { onSave, savedKeys } : {};

  // Every word or phrase looked up on this page, newest first, so a student
  // can save them to flashcards afterwards without finding them again. Kept
  // for the browser session, per assignment.
  const lookedKey = `looked:${a.id}`;
  const [looked, setLooked] = useState<{ term: string; translation: string }[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(lookedKey) ?? '[]'); } catch { return []; }
  });
  const onLookup = useCallback((term: string, translation: string) => {
    log.addLookup();
    setLooked(prev => {
      const next = [{ term, translation }, ...prev.filter(x => termKey(x.term) !== termKey(term))].slice(0, 200);
      try { sessionStorage.setItem(lookedKey, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, [log, lookedKey]);
  const overdue = a.due_date && a.due_date < today() && !progress?.completed_at;

  return (
    <div className="assignment">
      <div className="crumbs">
        <Link to={isStudent ? '/work' : '/assignments'}>← {isStudent ? 'All my work' : 'All assignments'}</Link>
        {!isStudent && <Link to={`/assign/${a.id}`} className="spacer-left">Edit</Link>}
      </div>

      <div className="badge">{KIND[a.kind].label}{a.due_date ? ` · due ${shortDate(a.due_date)}` : ''}</div>
      <h1>{a.title}</h1>
      <p className={`sub${overdue ? ' warn' : ''}`}>
        {requirement(a)}
        {' · '}
        {a.due_date ? `due ${shortDate(a.due_date)}` : 'due by the end of the week'}
      </p>

      {isStudent && <PassDots a={a} done={done} passes={passes} />}
      <RewardLine a={a} done={done} />
      {lockedToday && (
        <div className="notice good" style={{ marginBottom: 18 }}>
          ✓ You did today's {KIND[a.kind].verb}. Come back tomorrow for the next one!
          {a.kind !== 'grammar' && ' You can keep going for practice, but it won’t count until tomorrow.'}
        </div>
      )}

      {!isStudent && (
        <div className="notice small" style={{ marginBottom: 20 }}>
          {a.draft
            ? <><strong>Draft:</strong> students can't see this yet. </>
            : !isOut(a, today()) && <><strong>Scheduled:</strong> goes out to students {shortDate(goesOutOn(a))}. </>}
          Preview: this is what students see. Your own time here isn't recorded.
          {a.kind === 'story' && a.story_text && (
            <> How students do it: <strong>{storyPlanLabel(a)}</strong>{lessonPartFor(a, 0) ? `, with the lesson split into ${lessonPartFor(a, 0)!.of} parts, one per lesson read` : ''}. A lesson read counts when the lesson is finished: every
              question answered right (a miss comes back a few questions later){storyModesUsed(a).some(needsReadingTime) ? <>; a timed read needs at least <strong>{clock(minReadSeconds(a.story_text, a.read_wpm))}</strong> of
              active reading ({wordCount(a.story_text)} words at {paceLabel(a.read_wpm ?? DEFAULT_WPM).toLowerCase()} pace), paragraph by paragraph with the page held still (students with their own reading time on the Students page need more or less)</> : ''}.</>
          )}
        </div>
      )}

      {!isStudent && <AssignmentResults a={a} />}

      {a.instructions && (
        <div className="panel instructions">
          <TranslatableText text={a.instructions} source={source} target={target} onLookup={onLookup} {...saver} />
        </div>
      )}

      <AssignmentImages images={a.images} />

      {a.kind === 'grammar'
        ? <GrammarPart a={a} log={log} isStudent={isStudent} lockedToday={lockedToday} />
        : a.kind === 'video' && a.video_id
        ? (
          <>
            <VideoPart a={a} progress={progress} log={log} isStudent={isStudent} source={source} target={target} saver={saver} onLookup={onLookup} lockedToday={lockedToday} />
            {isStudent && !a.subtitles?.length && <SaveHeardWord source={source} target={target} onSave={onSave} savedKeys={savedKeys} />}
          </>
        )
        : <StoryPart a={a} progress={progress} log={log} isStudent={isStudent} source={source} target={target} saver={saver} onLookup={onLookup} lockedToday={lockedToday} />}

      {isStudent && looked.length > 0 && <LookedUp looked={looked} onSave={onSave} savedKeys={savedKeys} />}
    </div>
  );
}

/** The sand dollars this assignment pays, and what's been earned so far. */
function RewardLine({ a, done }: { a: Assignment; done: number }) {
  const per = a.sd_per_pass ?? 0, bonus = a.sd_on_complete ?? 0;
  if (!per && !bonus) return null;
  const n = a.required_count;
  const each = a.kind === 'video' ? 'watch' : a.kind === 'grammar' ? 'lesson' : 'read';
  const earned = per * Math.min(done, n) + (done >= n ? bonus : 0);
  const parts = [
    per ? `🪙 ${per} for each ${each}` : null,
    bonus ? `🪙 ${bonus} ${a.one_per_day && n > 1 ? `for doing it every day (${n} days)` : 'when you finish'}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="reward-line small">
      <span>{parts}</span>
      {done > 0 && <span className="reward-earned">You’ve earned 🪙 {earned}{done < n ? ` of ${per * n + bonus}` : ''}</span>}
    </div>
  );
}

/** "● ● ○" — one dot per required pass, with each finished one's date. */
function PassDots({ a, done, passes }: { a: Assignment; done: number; passes: Pass[] }) {
  return (
    <div className="pass-dots" aria-label={`${done} of ${a.required_count} done`}>
      {Array.from({ length: Math.max(a.required_count, done) }, (_, i) => {
        const p = passes.find(x => x.n === i + 1);
        return (
          <span key={i} className={`pass-dot${i < done ? ' on' : ''}${i >= a.required_count ? ' extra' : ''}`}
            title={p ? `${noun(a)} ${i + 1}: ${shortDate(p.finished_at)}, ${minutes(p.seconds)}` : undefined}>
            {i < done ? '✓' : i + 1}
          </span>
        );
      })}
      <span className="small muted">
        {done >= a.required_count
          ? `Done${done > a.required_count ? `, plus ${done - a.required_count} extra` : ''}. Extra ${noun(a)}s still count as review.`
          : `${done} of ${a.required_count} ${noun(a)}s done`}
      </span>
    </div>
  );
}

type Log = ReturnType<typeof useActivityLog>;

/** Re-render once a second, for live tallies that live in refs. */
function useSecondTick(on: boolean) {
  const [, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => set(n => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [on]);
}

// ── Video ───────────────────────────────────────────────────────────────────

function VideoPart({ a, progress, log, isStudent, source, target, saver, onLookup, lockedToday }: {
  a: Assignment; progress: Progress | undefined; log: Log; isStudent: boolean;
  source: string; target: string; saver: Saver; onLookup: (term: string, translation: string) => void;
  lockedToday: boolean;
}) {
  const { reloadPasses, refresh } = useClassroom();
  const [duration, setDuration] = useState<number | null>(progress?.video_duration ?? null);
  // The owner blocks this video outside YouTube: it is watched there instead,
  // with the Gibberfish Classroom extension doing what this page would.
  const [blocked, setBlocked] = useState(false);

  // ── Subtitles fetch themselves ───────────────────────────────────────────
  // An assignment saved without subtitles gets them the first time anyone
  // opens it; fetch-subtitles saves them on the assignment for the whole
  // class. "pending" means someone else's visit is fetching right now.
  const hasSubs = !!a.subtitles?.length;
  const knownNone = a.subtitles_status === 'none' || a.subtitles_status === 'wrong_language';
  const [subsState, setSubsState] = useState<'idle' | 'loading' | 'none' | 'failed'>('idle');
  useEffect(() => {
    if (hasSubs || knownNone) return;
    let cancelled = false;
    let tries = 0;
    const attempt = async () => {
      setSubsState('loading');
      const r = await ensureAssignmentSubtitles(a.id);
      if (cancelled) return;
      if (r.status === 'ok') { await refresh(); return; }
      if (r.status === 'pending' && ++tries < 6) { window.setTimeout(attempt, 5000); return; }
      setSubsState(r.status === 'none' || r.status === 'wrong_language' ? 'none' : 'failed');
    };
    void attempt();
    return () => { cancelled = true; };
  }, [a.id, hasSubs, knownNone, refresh]);
  const [error, setError] = useState<string | null>(null);
  const completing = useRef(false);
  useSecondTick(isStudent);

  const onWatched = useCallback((s: number, d: number) => {
    if (!isStudent) return;
    log.addVideoSeconds(s, d);
    setDuration(prev => prev ?? d);
  }, [isStudent, log]);

  const passPlayed = (progress?.pass_video ?? 0) + log.pendingVideo();
  const fraction = duration ? Math.min(passPlayed / (duration * VIDEO_DONE_FRACTION), 1) : 0;
  const done = progress?.completions ?? 0;

  useEffect(() => {
    if (!isStudent || lockedToday || completing.current || !duration || fraction < 1) return;
    completing.current = true;
    setError(null);
    log.complete()
      .then(async () => {
        await reloadPasses(a.id);
        burst();
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Could not save. Keep watching.'))
      // A refusal waits a little before trying again, so it cannot loop.
      .finally(() => window.setTimeout(() => { completing.current = false; }, 5000));
  }, [fraction, duration, isStudent, lockedToday, log, reloadPasses, a.id]);

  return (
    <>
      <YouTubePlayer
        videoId={a.video_id!} onWatched={onWatched} onPlayingChange={log.setPlaying} forTeacher={!isStudent}
        cues={a.subtitles} source={source} target={target} onLookup={onLookup} {...saver}
        onBlocked={() => setBlocked(true)}
      />
      {blocked && <WatchOnYouTube videoId={a.video_id!} isStudent={isStudent} />}
      {!hasSubs && subsState === 'loading' && (
        <div className="notice small" style={{ marginTop: 10 }}>Loading {languageName(source)} subtitles…</div>
      )}
      {!hasSubs && (knownNone || subsState === 'none') && !isStudent && (
        <div className="notice small" style={{ marginTop: 10 }}>
          This video has no {languageName(source)} subtitles on YouTube, so students see none (YouTube's own are
          always off, so they never see their own language). Click Edit to paste a transcript, or choose a video that
          has {languageName(source)} subtitles.
        </div>
      )}
      {!hasSubs && subsState === 'failed' && !isStudent && (
        <div className="notice small" style={{ marginTop: 10 }}>
          Couldn't load subtitles just now. They'll be tried again next time this is opened, or click Edit to paste them.
        </div>
      )}
      {isStudent && (
        <div className="meter">
          <div className="meter-head">
            <strong>Watch {Math.min(done + 1, Math.max(a.required_count, done + 1))}</strong>
            <span className="muted small">
              {duration ? `${clock(passPlayed)} of ${clock(duration * VIDEO_DONE_FRACTION)} played` : 'Press play'}
            </span>
          </div>
          <div className="progress"><i style={{ width: `${Math.round(fraction * 100)}%` }} /></div>
          <div className="small muted">
            Each watch counts once {Math.round(VIDEO_DONE_FRACTION * 100)}% of the video has played.
            Skipping ahead doesn't count. After one finishes, play it again from the start for the next.
          </div>
          {error && <div className="notice bad small" style={{ marginTop: 10 }}>{error}</div>}
        </div>
      )}
    </>
  );
}

// ── Story ───────────────────────────────────────────────────────────────────

type Saver = {
  onSave?: (term: string, translation: string) => Promise<void>;
  savedKeys?: Set<string>;
};

/**
 * Seconds of real reading credited to each paragraph during this read, and
 * whether the student has skipped ahead of where they are up to. Rules in
 * lib/reading.ts. Kept in sessionStorage under the pass number, so a reload
 * mid-read does not wipe it, and a new pass starts from zero.
 *
 * Each second, at most ONE paragraph is credited:
 *   - nothing, unless the student is active and the page has been held still
 *     (all movement over the last STILL_WINDOW_MS added up, so scrolling up and
 *     down, fast or slow, is never still);
 *   - the next unread paragraph, if it is mostly on screen;
 *   - otherwise a paragraph already read, if one is in the reading band (going
 *     back over it is reading too: the time counts, the paragraph is done);
 *   - otherwise nothing: parked further down the story, ahead of the reading.
 * `onSecond` is called for every credited second, and only those are sent as
 * reading time.
 */
function useReadingCredit(key: string, mins: number[], active: boolean, isActiveNow: () => boolean, onSecond: () => void) {
  const count = mins.length;
  const [dwell, setDwell] = useState<number[]>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) ?? 'null');
      if (Array.isArray(saved) && saved.length === count) return saved;
    } catch { /* fall through */ }
    return Array(count).fill(0);
  });
  const dwellRef = useRef(dwell);
  dwellRef.current = dwell;
  const [ahead, setAhead] = useState(false);
  const aheadTicks = useRef(0);
  const minsRef = useRef(mins);
  minsRef.current = mins;
  const onSecondRef = useRef(onSecond);
  onSecondRef.current = onSecond;

  // The key changes when a pass is finished: start the next one from zero.
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    setDwell(Array(count).fill(0));
  }, [key, count]);

  // Where the story sits on screen, a few times a second: how far it has
  // travelled lately, whichever element is doing the scrolling.
  const trail = useRef<{ t: number; y: number }[]>([]);
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => {
      const el = document.querySelector('.story');
      if (!el) return;
      const now = Date.now();
      trail.current.push({ t: now, y: el.getBoundingClientRect().top });
      trail.current = trail.current.filter(p => now - p.t <= STILL_WINDOW_MS + 300);
    }, 200);
    return () => window.clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => {
      if (!isActiveNow()) return;
      const vh = window.innerHeight;
      const pts = trail.current;
      let travel = 0;
      for (let i = 1; i < pts.length; i++) travel += Math.abs(pts[i].y - pts[i - 1].y);
      // Still: and long enough watched to say so (a fresh page has no trail yet).
      const still = pts.length >= 5 && travel < vh * STILL_MAX_TRAVEL;

      const ms = minsRef.current;
      const cur = dwellRef.current;
      const frontier = cur.findIndex((d, i) => d < ms[i]);
      const els = document.querySelectorAll<HTMLElement>('.story .tt > p[data-p]');
      const band = { top: vh * 0.1, bottom: vh * 0.9 };
      let credit = -1;
      let laterInView = false;
      let earlierInView = -1;
      els.forEach(el => {
        const i = Number(el.dataset.p);
        if (i >= count) return;
        const r = el.getBoundingClientRect();
        const seen = Math.min(r.bottom, band.bottom) - Math.max(r.top, band.top);
        if (seen <= 0) return;
        if (i === frontier) {
          // Mostly on screen: most of it, or most of the screen when it is taller.
          if (seen >= 0.6 * Math.min(r.height, band.bottom - band.top)) credit = i;
        } else if (frontier !== -1 && i > frontier) {
          laterInView = true;
        } else if (earlierInView === -1) {
          earlierInView = i;
        }
      });
      if (credit === -1 && earlierInView !== -1) credit = earlierInView;

      // Skipped ahead: the unread paragraph is off screen and a later one is on.
      aheadTicks.current = credit === -1 && laterInView && frontier !== -1 ? aheadTicks.current + 1 : 0;
      setAhead(aheadTicks.current >= 4);

      if (!still || credit === -1) return;
      const next = cur.slice();
      next[credit] += 1;
      dwellRef.current = next;
      setDwell(next);
      try { sessionStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
      onSecondRef.current();
    }, 1000);
    return () => window.clearInterval(t);
  }, [active, key, count, isActiveNow]);

  /** Bring the next unread paragraph back on screen. */
  const goToUnread = useCallback(() => {
    const i = dwellRef.current.findIndex((d, j) => d < minsRef.current[j]);
    document.querySelector(`.story .tt > p[data-p="${i}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return { dwell, ahead, goToUnread };
}


/** A story lesson's result, kept in this browser until the reading after it is done ('both' mode). */
type LessonDone = { correct: number; total: number; seconds: number };
/** In a story lesson there are no hearts: a missed question comes back this many questions later, and the
 *  lesson only finishes once every question has been answered right. */
const STORY_RETRY_AFTER = 3;

/**
 * The story lesson (lib/storyLesson.ts): every sentence both ways, then the
 * teacher's questions. No hearts: a miss comes back STORY_RETRY_AFTER questions
 * later. `onDone` gets the result (and the lesson seconds this pass) once every
 * question has been answered right.
 */
function StoryLessonPanel({ a, log, isStudent, source, target, lockedToday, done, heading, onDone }: {
  a: Assignment; log: Log; isStudent: boolean; source: string; target: string; lockedToday: boolean;
  done: number; heading: string; onDone: (r: LessonDone) => Promise<void>;
}) {
  const { passesFor, session } = useClassroom();
  const text = a.story_text ?? '';
  const asked = useMemo(() => cleanStoryQuestions(a.story_questions), [a.story_questions]);
  // Picking up where you left off, on any device (lib/lessonSaves.ts); teachers' previews never save.
  const lessonSave = useLessonSave(a.id, done, session?.user.id, isStudent);
  const all = useMemo(() => sentences(text).length, [text]);
  // A split lesson (story_split): this read covers one part of the story.
  const part = lessonPartFor(a, done);
  const [from, to] = partRange(all, part);
  const count = to - from;
  const withQs = !part || part.index === part.of - 1 ? asked.length : 0;
  const [phase, setPhase] = useState<'idle' | 'loading' | 'playing'>('idle');
  const [loaded, setLoaded] = useState({ done: 0, of: 0 });
  const [lesson, setLesson] = useState<Question[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const passes = isStudent ? passesFor(a.id) : [];
  const scored = passes.filter(p => p.total);
  const hasSaved = !!lessonSave.saved && (lessonSave.saved.done.length > 0 || lessonSave.saved.missed.length > 0);
  const lessonSecs = useRef(0);

  // Lesson time counts only while the lesson is open and the student is doing something.
  useEffect(() => {
    if (phase !== 'playing' || !isStudent) return;
    const t = window.setInterval(() => { if (log.isActive()) { log.addSeconds(1); lessonSecs.current += 1; } }, 1000);
    return () => window.clearInterval(t);
  }, [phase, isStudent, log]);

  // While the lesson is open the page underneath must not scroll.
  useEffect(() => {
    if (phase !== 'playing') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [phase]);

  const start = async () => {
    setError(null); setPhase('loading'); setLoaded({ done: 0, of: count });
    try {
      if (lessonSave.saved === undefined) await lessonSave.reload();
      const means = await meanings(a.id, text, source, target, (d, of) => setLoaded({ done: d, of }));
      // The same mix for a pass in progress (so resuming finds the same questions); a new one each pass.
      setLesson(buildStoryLesson(text, means, asked, languageName(target), done * 7919 + 13, part, { source, target }, !!a.story_one_way));
      setPhase('playing');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t get the lesson ready. Try again.');
      setPhase('idle');
    }
  };

  return (
    <>
      <div className="grammar-start panel story-start">
        <div className="grammar-owl" aria-hidden>🧩</div>
        <div className="grow">
          <strong>{!isStudent ? `Try the story lesson` : lockedToday ? 'Practice lesson' : hasSaved ? 'Pick up where you left off' : heading}{part ? ` · part ${part.index + 1} of ${part.of}` : ''}</strong>
          <span className="small muted">
            {lockedToday ? 'Today’s is done, so this one is just for practice. ' : ''}
            {part ? `Sentences ${from + 1}–${to} of the story` : 'Go through the whole story'}, sentence by sentence: what each one means in {languageName(target)}{a.story_one_way ? '' : `, then how to say it in ${languageName(source)}`}
            {withQs ? `, then ${withQs} question${withQs === 1 ? '' : 's'} about the story` : ''}.
            {' '}{count} sentence{count === 1 ? '' : 's'} · {count * (a.story_one_way ? 1 : 2) + withQs} questions
            {scored.length ? ` · best score ${scored.reduce((m, p) => Math.max(m, Math.round(((p.correct ?? 0) / (p.total ?? 1)) * 100)), 0)}%` : ''}
          </span>
          {phase === 'loading' && <span className="small">Getting the lesson ready… {loaded.of ? `${loaded.done}/${loaded.of}` : ''}</span>}
          {error && <span className="small bad">{error}</span>}
        </div>
        <button className="primary big" disabled={phase === 'loading' || count === 0} onClick={() => void start()}>
          {phase === 'loading' ? 'Loading…' : hasSaved ? 'Continue lesson' : 'Start lesson'}
        </button>
      </div>

      {scored.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head"><h3>Your lessons</h3></div>
          <div className="score-list">
            {scored.map(p => {
              const pct = Math.round(((p.correct ?? 0) / (p.total ?? 1)) * 100);
              return (
                <div key={p.n} className="score-row">
                  <span>Lesson {p.n}</span>
                  <span className="score-bar"><i style={{ width: `${pct}%` }} /></span>
                  <span className="mono small">{pct}%</span>
                  <span className="small muted">{shortDate(p.finished_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {phase === 'playing' && lesson && (
        <GrammarLesson
          questions={lesson}
          hearts={null}
          retryAfter={STORY_RETRY_AFTER}
          saved={lessonSave.saved}
          onSave={isStudent ? lessonSave.save : undefined}
          speakPrompt={q => (q.key.endsWith('-to') ? q.prompt : null)}
          title={a.title}
          language={source}
          onExit={() => { setPhase('idle'); setLesson(null); }}
          onComplete={async r => {
            if (!isStudent || lockedToday) return;
            await onDone({ correct: r.correct, total: r.total, seconds: lessonSecs.current });
          }}
        />
      )}
    </>
  );
}

/**
 * A story, done the teacher's way (assignments.story_mode, 20261044):
 *   read     timed reading only (StoryReading)
 *   lesson   the story lesson first, with the story hidden; finishing it with
 *            every question right IS the read, and then the story opens up
 *   free     the story open from the start, read with no time limit; the read
 *            counts when the lesson is finished
 *   both     the story lesson first, then the story opens with timed reading;
 *            the read counts when both are done
 * Once a student has finished one read, the story stays open for the next ones.
 */
function StoryPart(props: {
  a: Assignment; progress: Progress | undefined; log: Log; isStudent: boolean; source: string; target: string;
  saver: Saver; onLookup: (term: string, translation: string) => void; lockedToday: boolean;
}) {
  const { a, progress, log, isStudent, source, target, saver, onLookup, lockedToday } = props;
  const { reloadPasses } = useClassroom();
  // Each read can be done its own way (story_plan); the teacher can preview any of them.
  const [previewPass, setPreviewPass] = useState(0);
  const done = isStudent ? progress?.completions ?? 0 : previewPass;
  const mode = storyModeFor(a, done);
  const mixed = storyModesUsed(a).length > 1 || !!lessonPartFor(a, 0);
  const previewPicker = !isStudent && mixed && (
    <div className="row small" style={{ marginBottom: 12, gap: 8 }}>
      <span className="muted">Preview</span>
      <select value={previewPass} onChange={e => setPreviewPass(Number(e.target.value))} aria-label="Preview which read" style={{ width: 'auto' }}>
        {Array.from({ length: a.required_count }, (_, i) => { const pt = lessonPartFor(a, i); return <option key={i} value={i}>read {i + 1}: {STORY_MODE_NAME[storyModeFor(a, i)]}{pt ? ` (part ${pt.index + 1} of ${pt.of})` : ''}</option>; })}
      </select>
    </div>
  );
  const text = a.story_text ?? '';
  // 'both': the lesson's result waits here until the reading is done too.
  const doneKey = `storyLessonDone:${a.id}:${done}`;
  const [lessonDone, setLessonDone] = useState<LessonDone | null>(() => {
    try { const v = JSON.parse(localStorage.getItem(doneKey) ?? 'null'); return v && typeof v.total === 'number' ? v : null; } catch { return null; }
  });
  useEffect(() => {
    try { const v = JSON.parse(localStorage.getItem(doneKey) ?? 'null'); setLessonDone(v && typeof v.total === 'number' ? v : null); } catch { setLessonDone(null); }
  }, [doneKey]);
  const [justFinished, setJustFinished] = useState(false);

  if (mode === 'read') return <>{previewPicker}<StoryReading key={`read-${done}`} {...props} /></>;

  const open = mode === 'free' || !isStudent || done >= 1 || justFinished || (mode === 'both' && !!lessonDone);
  const story = (
    <>
      <div className="row story-tools">
        <span className="small muted">Tap a word, or drag across words, to translate.{isStudent && ' Save the ones you want to remember.'}</span>
        <span className="spacer" />
        <button className="small" onClick={() => speak(text, source)}>🔊 Listen</button>
        <button className="small" onClick={() => window.speechSynthesis?.cancel()}>■ Stop</button>
      </div>
      <div className="story panel">
        <TranslatableText text={text} source={source} target={target} onLookup={onLookup} {...saver} />
      </div>
    </>
  );
  const locked = (
    <div className="panel story-locked">
      <div className="big-emoji" aria-hidden>🔒📖</div>
      <strong>The story opens after the lesson</strong>
      <span className="small muted">{mode === 'both' ? 'Finish the story lesson, then read the whole story.' : 'Finish the story lesson to see the whole story.'}</span>
    </div>
  );

  if (mode === 'free') {
    return (
      <>
        {previewPicker}
        {story}
        <div style={{ marginTop: 16 }}>
          <StoryLessonPanel a={a} log={log} isStudent={isStudent} source={source} target={target} lockedToday={lockedToday} done={done}
            heading="When you’ve read it: the story lesson"
            onDone={async r => {
              await log.complete({ correct: r.correct, total: r.total });
              await reloadPasses(a.id);
            }} />
        </div>
        {!isStudent && (
          <div className="notice small" style={{ marginTop: 12 }}>
            Preview: your lessons here aren't recorded. Students can read the story for as long as they like; the read
            counts when they finish the lesson (every question answered right; a miss comes back a few questions later).
          </div>
        )}
      </>
    );
  }

  if (mode === 'lesson') {
    return (
      <>
        {previewPicker}
        <StoryLessonPanel a={a} log={log} isStudent={isStudent} source={source} target={target} lockedToday={lockedToday} done={done}
          heading={done >= 1 ? 'Story lesson' : 'Start with the story lesson'}
          onDone={async r => {
            await log.complete({ correct: r.correct, total: r.total });
            await reloadPasses(a.id);
            setJustFinished(true);
          }} />
        {!isStudent && (
          <div className="notice small" style={{ marginTop: 12 }}>
            Preview: your lessons here aren't recorded. Students see the story only after finishing the lesson (every question answered
            right; a miss comes back a few questions later), which counts as the read. The meanings come from Google Translate, made when a student starts the
            lesson; they're never saved in the database.
          </div>
        )}
        <div style={{ marginTop: 16 }}>{open ? story : locked}</div>
      </>
    );
  }

  // both: the lesson, then the reading.
  const lessonPart = (
    <StoryLessonPanel a={a} log={log} isStudent={isStudent} source={source} target={target} lockedToday={lockedToday} done={done}
      heading="Step 1: the story lesson"
      onDone={async r => {
        try { localStorage.setItem(doneKey, JSON.stringify(r)); } catch { /* fine */ }
        setLessonDone(r);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }} />
  );
  if (isStudent && !lessonDone && !lockedToday) {
    return <>{previewPicker}{lessonPart}<div style={{ marginTop: 16 }}>{open ? story : locked}</div></>;
  }
  return (
    <>
      {previewPicker}
      {isStudent && lessonDone && <div className="notice good small" style={{ marginBottom: 12 }}>✓ Story lesson done ({Math.round((lessonDone.correct / Math.max(lessonDone.total, 1)) * 100)}%). Step 2: read the whole story.</div>}
      {!isStudent && lessonPart}
      {!isStudent && <div className="notice small" style={{ margin: '12px 0' }}>Preview: students do the lesson first, then read the story with the reading time below. Nothing here is recorded.</div>}
      <StoryReading {...props} lesson={lessonDone} onFinished={() => { try { localStorage.removeItem(doneKey); } catch { /* fine */ } setLessonDone(null); }} />
    </>
  );
}

/**
 * The story with timed reading (paragraph by paragraph, the page held still):
 * a whole read in 'read' mode, the second half of one in 'both' (after the
 * lesson, whose score and seconds come in `lesson`).
 */
function StoryReading({ a, progress, log, isStudent, source, target, saver, onLookup, lockedToday, lesson, onFinished }: {
  a: Assignment; progress: Progress | undefined; log: Log; isStudent: boolean; source: string; target: string;
  saver: Saver; onLookup: (term: string, translation: string) => void; lockedToday: boolean;
  lesson?: LessonDone | null; onFinished?: () => void;
}) {
  const { reloadPasses, members, session } = useClassroom();
  // The story's pace, and this student's share of its time (the teacher's
  // preview uses the story's own pace).
  const wpm = a.read_wpm ?? DEFAULT_WPM;
  const pct = isStudent ? members.find(m => m.student_id === session?.user.id)?.read_time_pct ?? 100 : 100;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const text = a.story_text ?? '';
  const done = progress?.completions ?? 0;
  useSecondTick(true);

  const paras = useMemo(() => paragraphs(text).map(p => ({ words: wordCount(p) })), [text]);
  // After a lesson that went faster than the server's minimum for it (3 s a question), the rest is made up reading.
  const shortfall = lesson ? Math.max(0, Math.min(Math.max(3 * lesson.total, 20), 900) - lesson.seconds) : 0;
  const need = useMemo(() => minReadSeconds(text, wpm, pct) + shortfall, [text, wpm, pct, shortfall]);
  // What each paragraph needs; an empty one (a stray blank line) needs nothing.
  const mins = useMemo(() => paras.map(p => (p.words === 0 ? 0 : paragraphMinSeconds(p.words, wpm, pct))), [paras, wpm, pct]);
  // The teacher's preview runs the same checks, so the checklist they see is
  // live — it just never saves anything.
  const { dwell, ahead, goToUnread } = useReadingCredit(
    `read2:${a.id}:${done}${isStudent ? '' : ':preview'}`, mins, true, log.isActive, () => log.addSeconds(1),
  );

  // In 'both' mode the lesson's seconds are in this pass too; they aren't reading.
  const readSeconds = Math.max(0, (isStudent ? progress?.pass_seconds ?? 0 : 0) + log.pendingSeconds() - (lesson?.seconds ?? 0));
  const parasRead = mins.filter((m, i) => (dwell[i] ?? 0) >= m).length;
  const timeOk = readSeconds >= need;
  const parasOk = parasRead === paras.length;
  const ready = timeOk && parasOk;

  // ── What a student sees: ONE bar. ────────────────────────────────────────
  // Half of it is time, half is paragraphs, and it is full only when both are.
  // Students are not told what fills it — the point is to read, not to game a
  // checklist. The teacher gets the checklist (below).
  const progressPct = Math.round(
    ((Math.min(readSeconds / need, 1) + parasRead / Math.max(paras.length, 1)) / 2) * 100,
  );

  // ── Leaving early ────────────────────────────────────────────────────────
  // Only while a read this student still owes is under way.
  const started = readSeconds >= 5 || dwell.some(d => d > 0);
  const owesReads = done < a.required_count;
  const guard = isStudent && owesReads && started && !busy && !lockedToday;
  const leaveMessage = !parasOk
    ? 'You haven’t read all of the story yet. Read it all the way to the end before you go.'
    : !timeOk
      ? 'Spend a bit more time reading. Go back over the story slowly before you go.'
      : 'You’ve finished reading! Tap “I finished reading” before you leave, or this read won’t count.';

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    guard && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (!guard) return;
    // Closing the tab or typing a new address: browsers only allow their own
    // generic "Leave site?" wording here, never ours.
    const onBefore = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [guard]);

  const finish = async () => {
    setBusy(true); setError(null);
    try {
      await log.complete(lesson ? { correct: lesson.correct, total: lesson.total } : undefined);
      await reloadPasses(a.id);
      onFinished?.();
      burst();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="row story-tools">
        <span className="small muted">Tap a word, or drag across words, to translate.{isStudent && ' Save the ones you want to remember.'}</span>
        <span className="spacer" />
        <button className="small" onClick={() => speak(text, source)}>🔊 Listen</button>
        <button className="small" onClick={() => window.speechSynthesis?.cancel()}>■ Stop</button>
      </div>

      <div className="story panel">
        <TranslatableText text={text} source={source} target={target} onLookup={onLookup} {...saver} />
      </div>

      {isStudent ? (
        <div className="meter reading-meter simple">
          <div className="progress reading-bar" aria-label="Reading progress">
            <i style={{ width: `${ready ? 100 : Math.min(progressPct, 99)}%` }} />
          </div>
          {ahead && !ready && (
            <div className="notice small" style={{ margin: '10px 0 0' }}>
              Looks like you skipped ahead. <button className="small" onClick={goToUnread}>Go back to where you were reading</button>
            </div>
          )}
          {error && <div className="notice bad small" style={{ margin: '10px 0 0' }}>{error}</div>}
          <button className="primary" disabled={busy || !ready || lockedToday} onClick={finish}>
            {lockedToday ? 'Today’s read is done. Come back tomorrow' : busy ? 'Saving…' : ready ? 'I finished reading ✓' : 'Keep reading…'}
          </button>
        </div>
      ) : (
        <div className="meter reading-meter">
          <div className="meter-head">
            <strong>How a read is checked</strong>
            <span className="muted small">Only you see this. Students see a single bar that fills up.</span>
          </div>

          <div className="check">
            <span className={`tick${timeOk ? ' ok' : ''}`}>{timeOk ? '✓' : '○'}</span>
            <span className="grow">
              <span>Reading time: only while the page is held still on the story, never while scrolling around, and paused after {READING_IDLE_MS / 1000} seconds with no tapping or scrolling</span>
              <span className="progress"><i style={{ width: `${Math.min(100, (readSeconds / need) * 100)}%` }} /></span>
            </span>
            <span className="small muted mono">{clock(Math.min(readSeconds, need))} / {clock(need)}</span>
          </div>

          <div className="check">
            <span className={`tick${parasOk ? ' ok' : ''}`}>{parasOk ? '✓' : '○'}</span>
            <span className="grow">
              <span>Every paragraph read in order, each for its own share of the time</span>
              <span className="para-strip">
                {paras.map((p, i) => (
                  <i key={i} className={(dwell[i] ?? 0) >= mins[i] ? 'on' : ''}
                    style={{ flex: Math.max(p.words, 1) }} />
                ))}
              </span>
            </span>
            <span className="small muted mono">{parasRead} / {paras.length}</span>
          </div>

          <div className="check">
            <span className="tick" />
            <span className="grow">
              <span className="small muted">What the student sees</span>
              <span className="progress reading-bar"><i style={{ width: `${ready ? 100 : Math.min(progressPct, 99)}%` }} /></span>
            </span>
            <span className="small muted mono">{ready ? 100 : Math.min(progressPct, 99)}%</span>
          </div>
        </div>
      )}

      {blocker.state === 'blocked' && (
        <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="leave-title">
          <div className="modal">
            <div className="big-emoji" aria-hidden>📖</div>
            <h2 id="leave-title">Not quite done!</h2>
            <p>{leaveMessage}</p>
            <div className="modal-actions">
              <button className="primary" onClick={() => blocker.reset()} autoFocus>Keep reading</button>
              <button onClick={() => blocker.proceed()}>Leave anyway</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/**
 * Under a video: type a word or phrase you heard, see its translation, save it.
 * Videos have no tappable captions (YouTube does not expose them to an embed),
 * so this is how something heard in a video gets into the deck.
 */
function SaveHeardWord({ source, target, onSave, savedKeys }: {
  source: string; target: string;
  onSave: (term: string, translation: string) => Promise<void>;
  savedKeys: Set<string>;
}) {
  const [term, setTerm] = useState('');
  const [result, setResult] = useState<{ term: string; translation: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const look = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = term.trim();
    if (!t) return;
    setBusy(true); setMsg(null); setResult(null);
    try {
      const r = await translate(t, source, target);
      setResult({ term: t, translation: r.text });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Translation failed.' });
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!result) return;
    setBusy(true); setMsg(null);
    try {
      await onSave(result.term, result.translation);
      setMsg({ ok: true, text: `Saved “${result.term}” to your flashcards.` });
      setTerm(''); setResult(null);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not save.' });
    } finally { setBusy(false); }
  };

  const already = result && savedKeys.has(termKey(result.term));

  return (
    <div className="panel heard">
      <form className="row" onSubmit={look} style={{ flexWrap: 'nowrap' }}>
        <input placeholder="Heard a word? Type it here…" value={term} onChange={e => setTerm(e.target.value)} maxLength={200} />
        <button disabled={busy || !term.trim()}>Translate</button>
      </form>
      {result && (
        <div className="heard-result">
          <span className="grow"><strong>{result.term}</strong><span className="muted">{result.translation}</span></span>
          {already
            ? <span className="tt-saved">✓ In flashcards</span>
            : <button className="small tt-save" onClick={save} disabled={busy}>＋ Flashcard</button>}
        </div>
      )}
      {msg && <div className={`small ${msg.ok ? 'good-text' : 'tt-err'}`} style={{ marginTop: 8 }}>{msg.text}</div>}
    </div>
  );
}

/** "Words you looked up" — every lookup on this page, each one a tap from being a flashcard. */
function LookedUp({ looked, onSave, savedKeys }: {
  looked: { term: string; translation: string }[];
  onSave: (term: string, translation: string) => Promise<void>;
  savedKeys: Set<string>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const unsaved = looked.filter(x => !savedKeys.has(termKey(x.term)));

  const save = async (items: { term: string; translation: string }[]) => {
    setError(null);
    for (const x of items) {
      setBusy(x.term);
      try { await onSave(x.term, x.translation); } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not save.';
        if (!/already/i.test(msg)) { setError(msg); break; }
      }
    }
    setBusy(null);
  };

  return (
    <div className="panel looked-up">
      <div className="panel-head">
        <h3>Words you looked up</h3>
        {unsaved.length > 1 && (
          <button className="small tt-save" onClick={() => save(unsaved)} disabled={!!busy}>
            ＋ Save all {unsaved.length} to flashcards
          </button>
        )}
      </div>
      <div className="list">
        {looked.map(x => {
          const saved = savedKeys.has(termKey(x.term));
          return (
            <div key={x.term} className="item">
              <span className="grow"><span className="term">{x.term}</span><span className="gloss">{x.translation}</span></span>
              {saved
                ? <span className="tt-saved">✓ In flashcards</span>
                : <button className="small tt-save" onClick={() => save([x])} disabled={!!busy}>
                    {busy === x.term ? 'Saving…' : '＋ Flashcard'}
                  </button>}
            </div>
          );
        })}
      </div>
      {error && <div className="tt-err" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── Grammar ─────────────────────────────────────────────────────────────────

function GrammarPart({ a, log, isStudent, lockedToday }: {
  a: Assignment; log: Log; isStudent: boolean; lockedToday: boolean;
}) {
  const { reloadPasses, passesFor, active, session } = useClassroom();
  const [playing, setPlaying] = useState(false);
  // A lesson left part-way comes back exactly as it was, questions and all, on any device (lib/lessonSaves.ts).
  const lessonSave = useLessonSave(a.id, isStudent ? passesFor(a.id).length : 0, session?.user.id, isStudent);
  const resuming = !!lessonSave.saved?.questions?.length;
  const [previewLesson, setPreviewLesson] = useState(0);
  const cfg = a.grammar;
  const passes = isStudent ? passesFor(a.id) : [];
  const scored = passes.filter(p => p.total);
  const best = scored.reduce((m, p) => Math.max(m, Math.round(((p.correct ?? 0) / (p.total ?? 1)) * 100)), 0);

  // While a lesson is open the page underneath must not scroll.
  useEffect(() => {
    if (!playing) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [playing]);

  if (!cfg) return <div className="notice">This grammar assignment has no settings.</div>;

  // With a climb, each lesson's level depends on how many the student has finished.
  const from = cfg.difficulty ?? 3;
  const climbs = cfg.difficultyTo != null && cfg.difficultyTo !== from && a.required_count > 1;
  const lessonIdx = isStudent ? passes.length : previewLesson;
  const level = levelFor(cfg, lessonIdx, a.required_count);
  const L = levelInfo(level);

  return (
    <>
      <div className="topic-chips">
        {cfg.topics.map(t => (
          <span key={t} className="topic-chip"><span aria-hidden>{TOPICS[t].emoji}</span> {TOPICS[t].title}</span>
        ))}
      </div>

      <div className="grammar-start panel">
        <div className="grammar-owl" aria-hidden>🧩</div>
        <div className="grow">
          <strong>{!isStudent ? 'Try a lesson' : resuming ? 'Pick up where you left off' : lockedToday ? 'Practice lesson' : 'Ready for a lesson?'}</strong>
          <span className="small muted">
            {lockedToday ? 'Today’s lesson is done, so this one is just for practice. ' : ''}
            {lessonSize(cfg)} questions{hasMatching(cfg) ? ', each verb starting with its chart (match the pronouns)' : ''} · 3 hearts{builderOnly(cfg) ? '' : ` · level ${L.n} (${L.name.toLowerCase()})`}{scored.length ? ` · best score ${best}%` : ''}
          </span>
          {climbs && isStudent && lessonIdx < a.required_count && (
            <span className="small muted">Lesson {lessonIdx + 1} of {a.required_count}: each lesson gets a little harder.</span>
          )}
          {!isStudent && !builderOnly(cfg) && (
            <span className="small muted">
              {L.summary}.
              {climbs && (
                <> Preview{' '}
                  <select value={previewLesson} onChange={e => setPreviewLesson(Number(e.target.value))} aria-label="Preview which lesson">
                    {Array.from({ length: a.required_count }, (_, i) => (
                      <option key={i} value={i}>lesson {i + 1} (level {levelFor(cfg, i, a.required_count)})</option>
                    ))}
                  </select>
                </>
              )}
            </span>
          )}
        </div>
        <button className="primary big" disabled={isStudent && lessonSave.saved === undefined} onClick={() => setPlaying(true)}>{resuming ? 'Continue lesson' : 'Start lesson'}</button>
      </div>

      {!isStudent && (
        <div className="notice small" style={{ marginTop: 12 }}>
          Preview: your lessons here aren't recorded.
        </div>
      )}

      {scored.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head"><h3>Your lessons</h3></div>
          <div className="score-list">
            {scored.map(p => {
              const pct = Math.round(((p.correct ?? 0) / (p.total ?? 1)) * 100);
              return (
                <div key={p.n} className="score-row">
                  <span>Lesson {p.n}</span>
                  <span className="score-bar"><i style={{ width: `${pct}%` }} /></span>
                  <span className="mono small">{pct}%</span>
                  <span className="small muted">{shortDate(p.finished_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {playing && (
        <GrammarLesson
          config={cfg}
          level={level}
          title={a.title}
          language={active?.language ?? 'es'}
          saved={lessonSave.saved}
          onSave={isStudent ? lessonSave.save : undefined}
          keepQuestions
          onExit={() => setPlaying(false)}
          onComplete={async r => {
            if (!isStudent || lockedToday) return;
            await log.complete({ correct: r.correct, total: r.total });
            await reloadPasses(a.id);
          }}
        />
      )}
    </>
  );
}

/** For videos that only play on youtube.com: open them there, with the extension. */
function WatchOnYouTube({ videoId, isStudent }: { videoId: string; isStudent: boolean }) {
  const [installed, setInstalled] = useState(extensionInstalled);
  useEffect(() => {
    // The extension's script may land a moment after the page.
    const t = window.setTimeout(() => setInstalled(extensionInstalled()), 800);
    return () => window.clearTimeout(t);
  }, []);

  if (!isStudent) {
    return (
      <div className="notice small yt-only">
        <strong>This video only plays on YouTube.</strong> Students watch it there with the
        Gibberfish Classroom Chrome extension, which shows the subtitles and counts their
        watches just like this page. <a href={youtubeUrl(videoId)} target="_blank" rel="noreferrer">Open on YouTube</a>
      </div>
    );
  }

  return (
    <div className="panel yt-only">
      <strong>This video plays on YouTube</strong>
      {installed ? (
        <>
          <p className="small muted">Your subtitles, translations and watch tracking open with it, right under the video.</p>
          <a className="button primary-link" href={youtubeUrl(videoId)} target="_blank" rel="noreferrer">▶ Watch on YouTube</a>
        </>
      ) : (
        <>
          <p className="small muted">
            To get credit (and subtitles), you need the <strong>Gibberfish Classroom</strong> extension in Chrome.
            Install it, sign in with your classroom username, then come back and press Watch on YouTube.
          </p>
          <div className="row">
            {EXTENSION_URL
              ? <a className="button primary-link" href={EXTENSION_URL} target="_blank" rel="noreferrer">Get the extension</a>
              : <span className="small muted">The extension is almost ready. Your teacher will tell you when you can install it.</span>}
            <a className="button" href={youtubeUrl(videoId)} target="_blank" rel="noreferrer">Watch on YouTube</a>
          </div>
        </>
      )}
    </div>
  );
}
